import { z } from 'zod';
import type { EndReason, UserId } from '../../types';

export const BATTLESHIP_ID = 'battleship';

/** Côté de la grille (10 × 10). */
export const GRID_SIZE = 10;

export type ShipKind = 'carrier' | 'cruiser' | 'destroyer' | 'submarine' | 'torpedo';

export interface ShipSpec {
  kind: ShipKind;
  label: string;
  size: number;
}

/** Flotte classique française. */
export const FLEET: readonly ShipSpec[] = [
  { kind: 'carrier', label: 'Porte-avions', size: 5 },
  { kind: 'cruiser', label: 'Croiseur', size: 4 },
  { kind: 'destroyer', label: 'Contre-torpilleur', size: 3 },
  { kind: 'submarine', label: 'Sous-marin', size: 3 },
  { kind: 'torpedo', label: 'Torpilleur', size: 2 },
] as const;

export const FLEET_BY_KIND: Record<ShipKind, ShipSpec> = Object.fromEntries(
  FLEET.map((s) => [s.kind, s]),
) as Record<ShipKind, ShipSpec>;

/** Nombre total de cases occupées par une flotte complète. */
export const FLEET_CELLS = FLEET.reduce((n, s) => n + s.size, 0);

export type Orientation = 'h' | 'v';

/** Position proposée par le joueur pendant la phase de placement. */
export interface Placement {
  kind: ShipKind;
  x: number;
  y: number;
  orientation: Orientation;
}

/** Navire placé sur la grille, avec l'état des impacts case par case. */
export interface PlacedShip extends Placement {
  size: number;
  hits: boolean[];
}

export type ShotResult = 'miss' | 'hit' | 'sunk';

export interface Shot {
  x: number;
  y: number;
  result: ShotResult;
  /** Renseigné uniquement lorsque le navire est coulé. */
  kind?: ShipKind;
}

export interface PlayerBoard {
  ships: PlacedShip[];
  ready: boolean;
  /** Tirs reçus sur cette grille, dans l'ordre chronologique. */
  incoming: Shot[];
}

export type BattleshipPhase = 'placement' | 'playing' | 'finished';

export interface BattleshipConfig {
  /** Secondes accordées par coup ; `null` désactive le chronomètre. */
  turnSeconds: number | null;
  /** Secondes accordées pour placer sa flotte ; `null` désactive le chronomètre. */
  placementSeconds: number | null;
  /** Un coup au but redonne la main au tireur. */
  extraTurnOnHit: boolean;
}

export const DEFAULT_BATTLESHIP_CONFIG: BattleshipConfig = {
  turnSeconds: 60,
  placementSeconds: 180,
  extraTurnOnHit: false,
};

/**
 * État complet et autoritaire d'une partie. Il n'est jamais envoyé tel quel au
 * navigateur : `viewFor()` en produit une projection par joueur.
 */
export interface BattleshipState {
  version: 1;
  phase: BattleshipPhase;
  /** Les deux joueurs, dans l'ordre des sièges. */
  seats: UserId[];
  boards: Record<UserId, PlayerBoard>;
  /** Joueur devant agir ; `null` pendant le placement (les deux jouent) et en fin de partie. */
  turn: UserId | null;
  winner: UserId | null;
  endReason: EndReason | null;
  config: BattleshipConfig;
  /** Échéance du chronomètre courant (epoch ms) ; `null` si aucun chronomètre. */
  deadlineAt: number | null;
  moves: number;
}

/** Navire adverse coulé : sa position devient une information légitimement connue. */
export interface RevealedShip extends Placement {
  size: number;
}

/** Projection de l'état pour un joueur donné (brouillard de guerre appliqué). */
export interface BattleshipView {
  phase: BattleshipPhase;
  youId: UserId;
  opponentId: UserId;
  turn: UserId | null;
  winner: UserId | null;
  endReason: EndReason | null;
  config: BattleshipConfig;
  deadlineAt: number | null;
  moves: number;
  you: {
    ships: PlacedShip[];
    ready: boolean;
    /** Tirs reçus de l'adversaire. */
    incoming: Shot[];
    shipsRemaining: number;
  };
  opponent: {
    ready: boolean;
    /** Vos tirs sur la grille adverse, et eux seuls. */
    shots: Shot[];
    /** Navires adverses coulés, position révélée. */
    sunk: RevealedShip[];
    shipsRemaining: number;
  };
}

const coordinate = z.number().int().min(0).max(GRID_SIZE - 1);

export const placementSchema = z.object({
  kind: z.enum(['carrier', 'cruiser', 'destroyer', 'submarine', 'torpedo']),
  x: coordinate,
  y: coordinate,
  orientation: z.enum(['h', 'v']),
});

export const battleshipActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('place'), ships: z.array(placementSchema).length(FLEET.length) }),
  z.object({ type: z.literal('fire'), x: coordinate, y: coordinate }),
  z.object({ type: z.literal('resign') }),
]);

export type BattleshipAction = z.infer<typeof battleshipActionSchema>;
